import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { showNotification } from 'actions/notification';
import {
  useGetBotVersions,
  invalidateBotVersions,
  usePostBot,
  usePatchBot,
} from 'actions/chatbot';
import StatusBadge from 'screens/chatbot/components/status-badge';
import PublishButton from 'screens/chatbot/components/publish-button';
import VersionsPanel from 'screens/chatbot/components/versions-panel';
import { UnpublishButton, RestoreButton } from 'screens/chatbot/components/state-buttons';

import {
  FormContainer,
  FormHeader,
  FormTitle,
  Field,
  Label,
  Input,
  TextArea,
  ButtonRow,
  SaveButton,
  DeleteButton,
  SecondaryButtonRow,
  ScopeChipsContainer,
  ScopeChip,
  ScopeChipRemove,
  ModalOverlay,
  ModalBox,
  ModalTitle,
  ModalText,
  ModalActions,
  ModalCancelButton,
  ModalConfirmDeleteButton,
  TagInput,
} from './styled-comps';

const EDITABLE_FIELDS = ['name', 'system_prompt', 'kb_scope', 'guardrail_rules'];

function EditForm({ bot, onSaved, onSelectVersion, isNew = false }) {
  const dispatch = useDispatch();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [guardrailInput, setGuardrailInput] = useState('');

  const { get: getVersions, data: versionsData } = useGetBotVersions();

  const handleSaveComplete = (errorMessage, data) => {
    setSaving(false);
    if (errorMessage) {
      dispatch(
        showNotification({
          text: `Failed: ${errorMessage}`,
          duration: 5,
          showCloseIcon: true,
        }),
      );
      return;
    }
    if (bot && bot.bot_key) {
      invalidateBotVersions(bot.bot_key);
    }
    // Pass the fresh row (with id / bot_key) up — parent needs it to re-fetch
    // detail after a create, otherwise subsequent PATCH hits /bots/undefined/.
    onSaved && onSaved(data);
  };

  const { post: postBot } = usePostBot({ onComplete: handleSaveComplete });
  const { patch: patchBot } = usePatchBot({ onComplete: handleSaveComplete });

  const botKey = bot && bot.bot_key;
  useEffect(() => {
    if (botKey && !isNew) {
      getVersions(botKey);
    }
  }, [botKey, isNew, getVersions]);

  useEffect(() => {
    if (bot) {
      setForm({
        name: bot.name || '',
        system_prompt: bot.system_prompt || '',
        kb_scope: bot.kb_scope || '',
        guardrail_rules: bot.guardrail_rules || [],
      });
      setGuardrailInput('');
    }
  }, [bot]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleGuardrailKeyDown = e => {
    if (e.key === 'Enter' && guardrailInput.trim()) {
      e.preventDefault();
      const value = guardrailInput.trim();
      const current = form.guardrail_rules || [];
      if (!current.includes(value)) {
        handleChange('guardrail_rules', [...current, value]);
      }
      setGuardrailInput('');
    }
  };

  const removeGuardrailRule = rule => {
    handleChange(
      'guardrail_rules',
      (form.guardrail_rules || []).filter(r => r !== rule),
    );
  };

  const status = bot && bot.status;
  const isDraft = status === 'DRAFT';
  const isPublished = status === 'PUBLISHED';
  const isArchived = status === 'ARCHIVED';
  const readOnly = !isNew && !isDraft;

  const fieldPayload = () => {
    const payload = {};
    EDITABLE_FIELDS.forEach(key => {
      if (form[key] !== undefined) payload[key] = form[key];
    });
    return payload;
  };

  const handleSaveDraft = () => {
    setSaving(true);
    if (isNew) {
      postBot(fieldPayload());
    } else {
      patchBot(bot.id, fieldPayload());
    }
  };

  // Edit-published flow: create a DRAFT from the current PUBLISHED row.
  const handleEditPublished = () => {
    setSaving(true);
    postBot({ source_id: bot.id });
  };

  const handlePublish = () => {
    setSaving(true);
    patchBot(bot.id, { status: 'PUBLISHED' });
  };

  const handleUnpublish = () => {
    setSaving(true);
    patchBot(bot.id, { status: 'ARCHIVED' });
  };

  // Restore archived: POST {source_id} then PATCH status:PUBLISHED.
  const { post: postForRestore } = usePostBot({
    onComplete: (errorMessage, data) => {
      if (errorMessage || !data || !data.id) {
        setSaving(false);
        dispatch(
          showNotification({
            text: `Restore failed: ${errorMessage || 'unknown error'}`,
            duration: 5,
            showCloseIcon: true,
          }),
        );
        return;
      }
      patchBot(data.id, { status: 'PUBLISHED' });
    },
  });

  const handleRestore = () => {
    setSaving(true);
    postForRestore({ source_id: bot.id });
  };

  const handleDelete = () => {
    setShowDeleteConfirm(false);
    setSaving(true);
    patchBot(bot.id, { is_deleted: true });
  };

  const handleSelectVersion = version => {
    if (onSelectVersion) onSelectVersion(version);
  };

  if (!bot) return null;

  return (
    <FormContainer>
      <FormHeader>
        <FormTitle>{isNew ? 'New Bot' : bot.name}</FormTitle>
        {!isNew && <StatusBadge status={status} />}
      </FormHeader>

      <Field>
        <Label>Name</Label>
        <Input
          value={form.name || ''}
          onChange={e => handleChange('name', e.target.value)}
          placeholder="Bot name"
          disabled={readOnly}
        />
      </Field>

      <Field>
        <Label>System Prompt</Label>
        <TextArea
          rows={15}
          value={form.system_prompt || ''}
          onChange={e => handleChange('system_prompt', e.target.value)}
          placeholder="System prompt for the bot..."
          disabled={readOnly}
        />
      </Field>

      <Field>
        <Label>KB Scope (comma-separated tags)</Label>
        <Input
          value={form.kb_scope || ''}
          onChange={e => handleChange('kb_scope', e.target.value)}
          placeholder="e.g. billing,scheduling,onboarding"
          disabled={readOnly}
        />
      </Field>

      <Field>
        <Label>Guardrail Rules</Label>
        {!readOnly && (
          <TagInput
            value={guardrailInput}
            onChange={e => setGuardrailInput(e.target.value)}
            onKeyDown={handleGuardrailKeyDown}
            placeholder="Type a rule and press Enter"
          />
        )}
        {(form.guardrail_rules || []).length > 0 && (
          <ScopeChipsContainer>
            {form.guardrail_rules.map(rule => (
              <ScopeChip key={rule}>
                {rule}
                {!readOnly && (
                  <ScopeChipRemove onClick={() => removeGuardrailRule(rule)}>
                    &times;
                  </ScopeChipRemove>
                )}
              </ScopeChip>
            ))}
          </ScopeChipsContainer>
        )}
      </Field>

      <ButtonRow>
        {(isNew || isDraft) && (
          <SaveButton
            onClick={handleSaveDraft}
            disabled={saving}
            label={saving ? 'Saving...' : isNew ? 'Create Bot (draft)' : 'Save Draft'}
          />
        )}
        {isDraft && !isNew && (
          <PublishButton onPublish={handlePublish} disabled={saving} />
        )}
        {isPublished && (
          <>
            <SaveButton
              onClick={handleEditPublished}
              disabled={saving}
              label={saving ? 'Creating draft...' : 'Edit (creates draft)'}
            />
            <UnpublishButton onUnpublish={handleUnpublish} disabled={saving} noun="bot" />
          </>
        )}
        {isArchived && <RestoreButton onRestore={handleRestore} disabled={saving} />}
      </ButtonRow>

      {!isNew && isDraft && (
        <SecondaryButtonRow>
          <DeleteButton
            onClick={() => setShowDeleteConfirm(true)}
            disabled={saving}
            label="Delete"
          />
        </SecondaryButtonRow>
      )}

      {!isNew && (
        <VersionsPanel
          versions={versionsData}
          selectedId={bot.id}
          onSelect={handleSelectVersion}
        />
      )}

      {showDeleteConfirm && (
        <ModalOverlay onClick={() => setShowDeleteConfirm(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>Are you sure you want to delete {bot.name}?</ModalTitle>
            <ModalText>This action cannot be undone.</ModalText>
            <ModalActions>
              <ModalCancelButton
                onClick={() => setShowDeleteConfirm(false)}
                label="Cancel"
              />
              <ModalConfirmDeleteButton label="Delete" onClick={handleDelete} />
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}
    </FormContainer>
  );
}

export default EditForm;
